import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader } from '@/components/PageHeader';
import { KPICard } from '@/components/KPICard';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Plane, CheckSquare, AlertCircle, Clock, ArrowRight, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fmtTime, fmtDay } from '@/lib/dates';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from 'recharts';

export default function DashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/summary').then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="py-16 flex justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const c = data.cards;

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Hai, ${user.full_name.split(' ').slice(0, 2).join(' ')}`}
        subtitle={`Selamat datang kembali di PROAKTIF. Berikut ringkasan aktivitas Bagian Prokopim.`}
        action={
          (user.role === 'admin' || user.role === 'operator') && (
            <>
              <Link to="/agenda/baru"><Button className="bg-[#DC2626] hover:bg-[#B91C1C]"><Calendar className="w-4 h-4 mr-2" />Tambah Agenda</Button></Link>
              <Link to="/sppd/baru"><Button variant="outline"><Plane className="w-4 h-4 mr-2" />Registrasi SPPD</Button></Link>
            </>
          )
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard label="Agenda Hari Ini" value={c.agenda_hari_ini} icon={Calendar} accent="red" />
        <KPICard label="Agenda Besok" value={c.agenda_besok} icon={Clock} accent="gold" />
        <KPICard label="Pejabat Sedang Dinas" value={c.pejabat_sedang_dinas} icon={Plane} accent="blue" />
        <KPICard label="Menunggu Approval" value={c.draft_pending} icon={CheckSquare} accent="slate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-premium">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold">Agenda Hari Ini</CardTitle>
              <Link to="/agenda" className="text-xs font-semibold text-[#DC2626] hover:underline inline-flex items-center gap-1">Semua <ArrowRight className="w-3 h-3" /></Link>
            </div>
          </CardHeader>
          <CardContent>
            {data.today_agendas.length === 0 ? (
              <EmptyState title="Tidak ada agenda hari ini" />
            ) : (
              <div className="divide-y divide-slate-100">
                {data.today_agendas.map((a) => (
                  <Link to={`/agenda/${a.id}`} key={a.id} className="flex items-center gap-4 py-3 hover:bg-slate-50 px-2 -mx-2 rounded">
                    <div className="w-16 text-center">
                      <div className="text-lg font-bold tabular text-slate-900">{fmtTime(a.tanggal_mulai)}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">WITA</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-slate-800 truncate">{a.nama_acara}</div>
                      <div className="text-xs text-slate-500 truncate"><MapPin className="w-3 h-3 inline -mt-0.5" /> {a.lokasi}</div>
                    </div>
                    <StatusBadge status={a.status} type="agenda" />
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader>
            <CardTitle className="text-base font-bold">Pejabat Sedang Dinas</CardTitle>
          </CardHeader>
          <CardContent>
            {data.sedang_dinas.length === 0 ? (
              <EmptyState title="Tidak ada pejabat sedang dinas" />
            ) : (
              <div className="space-y-2.5">
                {data.sedang_dinas.map((s) => (
                  <Link to={`/sppd/${s.id}`} key={s.id} className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-slate-50">
                    <div className="w-8 h-8 rounded bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
                      <Plane className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-xs text-slate-800 truncate">{s.pejabat_nama}</div>
                      <div className="text-[11px] text-slate-500 truncate"><MapPin className="w-3 h-3 inline" /> {s.tujuan_kota}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Tren Agenda 6 Bulan</CardTitle></CardHeader>
          <CardContent style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.monthly_agenda}>
                <defs>
                  <linearGradient id="dashRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#DC2626" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#DC2626" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="bulan" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip />
                <Area type="monotone" dataKey="jumlah" stroke="#DC2626" strokeWidth={2} fill="url(#dashRed)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Tren SPPD 6 Bulan</CardTitle></CardHeader>
          <CardContent style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly_sppd}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="bulan" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip />
                <Bar dataKey="jumlah" fill="#FBBF24" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
